import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@frekuent/orbit/components';
import SettingsIcon from '@/legacy/components/ui/Icons/SettingsIcon';
import { Skeleton } from '@/shared/ui/skeleton';
import { useCompanySwitch } from '../hooks/useCompanySwitch';
import type { DelegationSubtitle } from '../domain/delegationSubtitle';

type CompanySwitchProps = {
	collapsed: boolean;
	toggleCollapse: () => void;
};

const useSubtitleText = (subtitle: DelegationSubtitle): string | null => {
	const { t } = useTranslation('companySwitch');

	switch (subtitle.kind) {
		case 'none':
			return null;
		case 'all':
			return t('allDelegations');
		case 'name':
			return subtitle.name;
		case 'count':
			return t('delegationCount', { count: subtitle.count });
	}
};

const collapsedTileClassName =
	'nav-item-surface nav-item-collapsed flex items-center justify-center transition-colors hover:bg-secondary-ultra-light';

// Orbit's <Button variant='default' size='icon'> is a 44px full circle (w-11 rounded-full) —
// too big and too round for the sidebar's 32px/8px-radius collapsed tiles. This reproduces the
// "default" variant's bordered look (border-secondary, hover drops the border) at the tile size
// every other collapsed sidebar item already uses, matching the expanded gear's local styling too.
const collapsedSettingsButtonClassName =
	'nav-item-surface nav-item-collapsed flex items-center justify-center border border-secondary text-secondary transition-colors hover:bg-secondary-ultra-light hover:border-transparent';

const CompanySwitch = ({ collapsed, toggleCollapse }: CompanySwitchProps) => {
	const { t } = useTranslation('companySwitch');
	const {
		isReady,
		companyName,
		initials,
		avatarClass,
		isLoadingCompanies,
		subtitle,
		isInteractive,
		onSwitchClick,
		hasSettingsAccess,
		onSettingsClick,
	} = useCompanySwitch();
	const subtitleText = useSubtitleText(subtitle);

	if (!isReady) return null;

	const avatar = isLoadingCompanies ? (
		<Skeleton
			circle
			width={30}
			height={30}
		/>
	) : (
		<div
			className={clsx(
				'w-[30px] h-[30px] shrink-0 rounded-[7px] flex items-center justify-center text-white text-[15px] font-medium uppercase',
				avatarClass,
			)}
		>
			{initials}
		</div>
	);

	if (collapsed) {
		const tooltipContent = subtitleText ? `${companyName} · ${subtitleText}` : (companyName ?? '');

		return (
			<div className='flex flex-col gap-2'>
				<Tooltip
					content={tooltipContent}
					placement='right'
					size='small'
				>
					<button
						type='button'
						onClick={toggleCollapse}
						aria-label={t('changeCompany')}
						className={collapsedTileClassName}
					>
						{avatar}
					</button>
				</Tooltip>
				{hasSettingsAccess && (
					<Tooltip
						content={t('settingsLabel')}
						placement='right'
						size='small'
					>
						<button
							type='button'
							onClick={onSettingsClick}
							aria-label={t('settingsLabel')}
							className={collapsedSettingsButtonClassName}
						>
							<SettingsIcon
								width={16}
								height={16}
							/>
						</button>
					</Tooltip>
				)}
			</div>
		);
	}

	const leftSegmentClassName = 'flex-1 min-w-0 flex items-center gap-2 px-3 py-2';
	const leftSegmentContent = (
		<>
			{avatar}
			<div className='min-w-0 flex flex-col'>
				<span className='text-base font-medium text-primary truncate'>{companyName}</span>
				{subtitleText && (
					<span className='text-xs text-tertiary truncate first-letter-uppercase'>
						{subtitleText}
					</span>
				)}
			</div>
		</>
	);

	return (
		<div className='bg-light border border-tertiary rounded-small overflow-hidden flex'>
			{isInteractive ? (
				<button
					type='button'
					onClick={onSwitchClick}
					aria-label={t('changeCompany')}
					className={twMerge(
						leftSegmentClassName,
						'text-left transition-colors hover:bg-secondary-ultra-light',
					)}
				>
					{leftSegmentContent}
				</button>
			) : (
				<div className={leftSegmentClassName}>{leftSegmentContent}</div>
			)}
			{hasSettingsAccess && (
				<>
					<div className='w-px self-stretch bg-tertiary' />
					<button
						type='button'
						onClick={onSettingsClick}
						aria-label={t('settingsLabel')}
						className='w-[34px] flex items-center justify-center transition-colors hover:bg-secondary-ultra-light'
					>
						<SettingsIcon
							width={16}
							height={16}
						/>
					</button>
				</>
			)}
		</div>
	);
};

export default CompanySwitch;
